"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFeedbackPanelExpansion, useFeedbackPanelHeader, resolveAreaWithPage } from "@/entities/feature-announcement-feedback";
import { useMyFeedbackPanel } from "../model/use-my-feedback-panel";
import { FeedbackTicketList } from "./feedback-ticket-list";
import { FeedbackTicketDetail } from "./feedback-ticket-detail";
import { StatusBadge } from "./status-badge";

/**
 * The compact "Your feedback" widget tab. Selecting a ticket asks the shell to
 * widen (via the expansion context) and reveals the conversation as a second
 * pane beside the list — the list stays put. On narrow viewports (where the
 * shell can't widen) it falls back to list-then-detail with a back control.
 *
 * The conversation column deliberately has NO bar of its own: the widget card is
 * only ~90vh tall and every fixed row above the thread comes straight off the
 * space the customer reads Cova's reply in. The ticket's identity (back control,
 * area, status) is published up to the shell instead and rendered inside the
 * shell's existing "Feedback" header row, which costs zero extra height.
 */
export function MyFeedbackPanel() {
  const $ = _c(38);
  const panel = useMyFeedbackPanel();
  const setExpanded = useFeedbackPanelExpansion();
  const setHeaderSlot = useFeedbackPanelHeader();
  const replyError = panel.replyError ? "Could not send your reply. Please try again." : null;
  const hasSelection = !!panel.selectedId;
  let t0;
  let t1;
  if ($[0] !== hasSelection || $[1] !== setExpanded) {
    t0 = () => {
      setExpanded(hasSelection);
    };
    t1 = [hasSelection, setExpanded];
    $[0] = hasSelection;
    $[1] = setExpanded;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] !== setExpanded) {
    t2 = () => () => setExpanded(false);
    t3 = [setExpanded];
    $[4] = setExpanded;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  const openFeedback = hasSelection ? panel.thread.feedback : null;
  let t4;
  if ($[7] !== openFeedback) {
    t4 = openFeedback ? resolveAreaWithPage(openFeedback.featureKey, openFeedback.originFeatureKey) : null;
    $[7] = openFeedback;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const areaText = t4;
  const status = openFeedback?.status ?? null;
  const back = panel.back;
  let t5;
  let t6;
  if ($[9] !== areaText || $[10] !== back || $[11] !== setHeaderSlot || $[12] !== status) {
    t5 = () => {
      if (!areaText || !status) {
        setHeaderSlot(null);
        return;
      }
      setHeaderSlot(<><button type="button" onClick={back} aria-label="Back to your feedback list" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"><ArrowLeft className="h-3.5 w-3.5" /></button><h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-[#091E42]">{areaText}</h2><StatusBadge status={status} /></>);
      return () => setHeaderSlot(null);
    };
    t6 = [areaText, status, back, setHeaderSlot];
    $[9] = areaText;
    $[10] = back;
    $[11] = setHeaderSlot;
    $[12] = status;
    $[13] = t5;
    $[14] = t6;
  } else {
    t5 = $[13];
    t6 = $[14];
  }
  useEffect(t5, t6);
  const t7 = hasSelection ? "hidden min-[481px]:flex min-[481px]:w-[300px] min-[481px]:shrink-0 min-[481px]:border-r min-[481px]:border-gray-100" : "flex-1";
  let t8;
  if ($[15] !== t7) {
    t8 = cn("flex min-h-0 flex-col", t7);
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== panel.error || $[18] !== panel.isLoading || $[19] !== panel.openThread || $[20] !== panel.rows || $[21] !== panel.selectedId) {
    t9 = <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3"><FeedbackTicketList rows={panel.rows} selectedId={panel.selectedId} onSelect={panel.openThread} isLoading={panel.isLoading} error={panel.error} /></div>;
    $[17] = panel.error;
    $[18] = panel.isLoading;
    $[19] = panel.openThread;
    $[20] = panel.rows;
    $[21] = panel.selectedId;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t8 || $[24] !== t9) {
    t10 = <div className={t8}>{t9}</div>;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== hasSelection || $[27] !== panel.isReplying || $[28] !== panel.replyAttachments || $[29] !== panel.replyDraft || $[30] !== panel.sendReply || $[31] !== panel.setReplyDraft || $[32] !== panel.thread || $[33] !== replyError) {
    t11 = hasSelection && <div className="flex min-h-0 flex-1 flex-col"><div className="min-h-0 flex-1 overflow-hidden px-4 py-3"><FeedbackTicketDetail hasSelection={true} hideHeader={true} thread={panel.thread} replyDraft={panel.replyDraft} setReplyDraft={panel.setReplyDraft} onSend={panel.sendReply} isReplying={panel.isReplying} replyError={replyError} replyAttachments={panel.replyAttachments} /></div></div>;
    $[26] = hasSelection;
    $[27] = panel.isReplying;
    $[28] = panel.replyAttachments;
    $[29] = panel.replyDraft;
    $[30] = panel.sendReply;
    $[31] = panel.setReplyDraft;
    $[32] = panel.thread;
    $[33] = replyError;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t10 || $[36] !== t11) {
    t12 = <div className="flex h-full min-h-0">{t10}{t11}</div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  return t12;
}